import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { fetchPublicSettings } from '../../../../services/settings.api'
import {
  cancelExam,
  fetchExamStatus,
  requestExam,
} from '../../../../services/academyExam.api'
import {
  GRADUATION_REQUIREMENTS_DEFAULTS,
  evaluateGraduationItems,
  pickGraduationFromSettings,
} from '../../../../utils/graduationRequirements'
import { selectCharacter } from '../../ViewInventory/inventorySlice'
import { useSheetReviewContext } from './SheetReviewContext'
import { getCharacterId } from '../../../../utils/characterId'

/**
 * Checklist de graduación + solicitud de examen (Estudiante F).
 */
export default function GraduationRequirementsPanel({ character = {} }) {
  const { slug } = useParams()
  const storeChar = useSelector(selectCharacter)
  // Prefer prop/context actor (characterId); else viewed sheet from Redux (id/characterId)
  const char =
    getCharacterId(character) != null ? character : storeChar || character
  const { isOwner } = useSheetReviewContext()

  const [config, setConfig] = useState(GRADUATION_REQUIREMENTS_DEFAULTS)
  const [examMeta, setExamMeta] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null
    fetchPublicSettings(ctrl?.signal)
      .then((settings) => {
        if (cancelled) return
        setConfig(pickGraduationFromSettings(settings))
      })
      .catch(() => {
        if (!cancelled) setConfig(GRADUATION_REQUIREMENTS_DEFAULTS)
      })
    return () => {
      cancelled = true
      ctrl?.abort()
    }
  }, [])

  const refreshExam = useCallback(async () => {
    if (!slug || !isOwner) return
    try {
      const data = await fetchExamStatus(slug)
      setExamMeta(data)
    } catch {
      /* ignore — panel still works from character props */
    }
  }, [slug, isOwner])

  useEffect(() => {
    refreshExam()
  }, [refreshExam])

  const evalSource = useMemo(() => {
    const fromExam = examMeta?.character || {}
    return {
      ...char,
      ...fromExam,
      history: fromExam.history ?? char?.history,
      level: fromExam.level ?? char?.level,
      learnedTechniqueIds: fromExam.learnedTechniqueIds ?? char?.learnedTechniqueIds,
      academyBasicTechniqueIds: fromExam.academyBasicTechniqueIds ?? char?.academyBasicTechniqueIds,
      academySimulacroDone:
        fromExam.academySimulacroDone ?? char?.academySimulacroDone ?? false,
      academyPostsDone: fromExam.academyPostsDone ?? char?.academyPostsDone ?? false,
    }
  }, [char, examMeta])

  const { intro, items } = useMemo(
    () =>
      examMeta?.items
        ? { intro: examMeta.intro, items: examMeta.items }
        : evaluateGraduationItems(config, evalSource),
    [config, evalSource, examMeta]
  )

  const allDone = items.every((i) => i.done)
  const doneCount = items.filter((i) => i.done).length
  const status =
    examMeta?.graduationExamStatus ??
    char?.graduationExamStatus ??
    null
  const sensei = examMeta?.sensei || null
  const isRequested = status === 'requested'
  const isClosed = status === 'closed' || Boolean(char?.academyGraduatedAt)

  const onRequest = async () => {
    if (!slug || busy) return
    setBusy(true)
    setError(null)
    try {
      const data = await requestExam(slug)
      setExamMeta(data)
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'No se pudo solicitar el examen')
    } finally {
      setBusy(false)
    }
  }

  const onCancel = async () => {
    if (!slug || busy) return
    if (!window.confirm('¿Cancelar la solicitud de examen?')) return
    setBusy(true)
    setError(null)
    try {
      const data = await cancelExam(slug)
      setExamMeta(data)
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'No se pudo cancelar')
    } finally {
      setBusy(false)
    }
  }

  let cta = null
  if (isClosed) {
    cta = (
      <button type="button" className="char-btn-accent" disabled>
        Graduado
      </button>
    )
  } else if (isRequested) {
    cta = (
      <>
        <button type="button" className="char-btn-accent" disabled>
          Examen solicitado
        </button>
        {isOwner ? (
          <button
            type="button"
            className="char-btn-ghost"
            disabled={busy}
            onClick={onCancel}
          >
            Cancelar
          </button>
        ) : null}
      </>
    )
  } else if (isOwner) {
    cta = (
      <button
        type="button"
        className="char-btn-accent"
        disabled={!allDone || busy}
        title={
          allDone
            ? 'Solicitar examen de graduación'
            : 'Disponible al completar todos los requisitos'
        }
        onClick={onRequest}
      >
        {busy ? 'Solicitando…' : 'Solicitar examen'}
      </button>
    )
  } else {
    cta = (
      <button type="button" className="char-btn-accent" disabled>
        Solicitar examen
      </button>
    )
  }

  return (
    <article className="char-card char-card--section char-graduation" aria-label="Requisitos de graduación">
      <header className="char-section-header">
        <span className="char-section-header__ornament" aria-hidden="true" />
        <h3 className="char-section-header__title">Requisitos de graduación</h3>
        <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
      </header>

      <p className="char-graduation__intro">{intro}</p>

      <ul className="char-graduation__list">
        {items.map((item) => (
          <li
            key={item.key}
            className={`char-graduation__item${item.done ? ' char-graduation__item--done' : ''}`}
          >
            <span className="char-graduation__check" aria-hidden="true">
              {item.done ? '✓' : '○'}
            </span>
            <div className="char-graduation__body">
              <span className="char-graduation__label">{item.label}</span>
              <span className="char-graduation__detail">{item.detail}</span>
            </div>
          </li>
        ))}
      </ul>

      {isRequested && sensei ? (
        <p className="char-graduation__sensei">
          Sensei asignado: <strong>{sensei.name}</strong>
          <span className="char-graduation__hint"> — pendiente de un sensei del staff</span>
        </p>
      ) : null}

      {error ? <p className="char-graduation__error">{error}</p> : null}

      <div className="char-graduation__footer">
        <span className="char-graduation__progress">
          {doneCount} / {items.length} requisitos
        </span>
        {cta}
        {!allDone && !isRequested && !isClosed ? (
          <span className="char-graduation__hint">Disponible cuando completes todos los requisitos.</span>
        ) : null}
      </div>
    </article>
  )
}
